/**
 * Typed HTTP client shared by the desktop, owner PWA and SaaS admin.
 * - Errors are ApiError with a stable `code`; UI maps codes to text.
 * - A network failure is ApiError('NETWORK') so offline logic can branch on it.
 * - `auth` supplies the access token and knows how to renew it once on 401.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isNetwork(): boolean {
    return this.code === 'NETWORK';
  }
}

export interface AuthProvider {
  /** Current access token, or null when signed out. */
  getAccessToken(): string | null | Promise<string | null>;
  /** Called once after a 401; returns true if a new token is available. */
  renew?(): Promise<boolean>;
  /**
   * Why there is no token, when the server said so (a disabled device). The
   * request then fails with that answer instead of a bare «Требуется вход».
   */
  failure?(): ApiError | null;
}

export interface ApiClientOptions {
  baseUrl: string;
  auth?: AuthProvider;
  /** Extra headers on every request (X-Business-Id, X-Employee-Id, X-App-Version). */
  headers?: () => Record<string, string | null | undefined>;
  timeoutMs?: number;
  fetch?: typeof fetch;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export class ApiClient {
  constructor(private readonly options: ApiClientOptions) {}

  get<T>(
    path: string,
    query?: Record<string, string | number | boolean | null | undefined>,
  ): Promise<T> {
    return this.request<T>('GET', path + toQuery(query));
  }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }
  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }
  delete<T = void>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  async request<T>(method: Method, path: string, body?: unknown, retried = false): Promise<T> {
    const { baseUrl, auth, timeoutMs = 20_000 } = this.options;
    const doFetch = this.options.fetch ?? fetch;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    for (const [k, v] of Object.entries(this.options.headers?.() ?? {})) if (v) headers[k] = v;
    const token = await auth?.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    else {
      const refused = auth?.failure?.();
      if (refused) throw refused;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let res: Response;
    try {
      res = await doFetch(baseUrl.replace(/\/$/, '') + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
    } catch {
      throw new ApiError(0, 'NETWORK', 'Нет связи с сервером');
    } finally {
      clearTimeout(timer);
    }

    if (res.status === 401 && !retried && auth?.renew && token) {
      if (await auth.renew()) return this.request<T>(method, path, body, true);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!res.ok) {
      const err = (
        json as {
          error?: { code?: string; message?: string; fields?: Record<string, string> };
        } | null
      )?.error;
      throw new ApiError(
        res.status,
        err?.code ?? `HTTP_${res.status}`,
        err?.message ?? 'Не удалось выполнить операцию',
        err?.fields ?? {},
      );
    }
    return json as T;
  }
}

function toQuery(query?: Record<string, string | number | boolean | null | undefined>): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query))
    if (v !== null && v !== undefined && v !== '') params.set(k, String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export * from './sessions';
export type * from './types';
export type * from './catalog-types';
export type * from './owner-types';
